import { Message } from "primereact/message";
import { useEffect, useState } from "react";
import { useGetBenefitWorklistQuery } from "store/queries/benefitComparison";
import { IWorklistFilters } from "types/BenefitComparison/BenefitComparison";

import {
  computedAtText,
  FRESHNESS_POLL_MS,
  pendingConfigText,
  REBUILDING_TEXT,
  rescoringText,
} from "./benefitComparisonWording";

/**
 * CLE-2820 follow-up — HOW OLD ARE THE NUMBERS ON THIS SCREEN.
 *
 * 🚨 THE SCREEN USED TO SAY NOTHING, AND THAT WAS WRONG. Its cluster counts come from a cached
 * aggregate, and the aggregate is rebuilt when the ENGINE's answers change — after a configuration
 * save that means once the debounced re-score has landed, seconds later. In between, the worklist
 * shows the PREVIOUS configuration's numbers, and the person looking is the person who just changed
 * it. So: name the account, say the numbers predate the change, and say when it was saved.
 *
 * 🚨 IT SUBSCRIBES TO THE SAME QUERY AS THE TAB rather than taking the data as a prop, and that is
 * deliberate on two counts. RTK Query serves both subscribers from ONE cache entry, so this adds no
 * request; and the poll can then live here, turned on only while something is actually moving,
 * without the tab having to know about it. The footprint in `BenefitComparisonTab.tsx` is one
 * import and one line on purpose — that file is edited often and by several people at once.
 */
export function BenefitWorklistFreshness({
  filters,
}: {
  filters: IWorklistFilters;
}) {
  // 🚨 THE POLL IS ON ONLY WHILE SOMETHING IS ACTUALLY MOVING. This screen is all-time and its
  // numbers change only when somebody changes configuration, so a standing poll would be a full
  // worklist read every five seconds for nothing. It turns itself off again when the answer comes
  // back with nothing pending, which is how the page updates itself without a refresh.
  const [moving, setMoving] = useState(false);
  const { data } = useGetBenefitWorklistQuery(filters, {
    pollingInterval: moving ? FRESHNESS_POLL_MS : 0,
  });

  const freshness = data?.data.freshness;
  const shouldPoll =
    (freshness?.pendingAccounts.length ?? 0) > 0 ||
    (freshness?.rebuilding ?? false) ||
    Boolean(freshness?.rescoring);
  useEffect(() => setMoving(shouldPoll), [shouldPoll]);

  if (!freshness) return null;

  // "Rerun all clusters" is running in the background: it is the newest thing anybody asked for and
  // it outlasts the other two, so it is the one line shown while it runs.
  if (freshness.rescoring) {
    return (
      <Message
        severity="info"
        className="mb-3 w-full justify-content-start"
        data-testid="benefitWorklist.freshness.rescoring"
        text={rescoringText(freshness.rescoring)}
      />
    );
  }

  const pendingText = pendingConfigText(freshness.pendingAccounts);
  if (pendingText) {
    return (
      <Message
        severity="info"
        className="mb-3 w-full justify-content-start"
        data-testid="benefitWorklist.freshness.pending"
        text={pendingText}
      />
    );
  }

  if (freshness.rebuilding) {
    return (
      <Message
        severity="info"
        className="mb-3 w-full justify-content-start"
        data-testid="benefitWorklist.freshness.rebuilding"
        text={REBUILDING_TEXT}
      />
    );
  }

  const asOf = computedAtText(freshness.computedAt);
  if (!asOf) return null;
  return (
    <div
      className="text-color-secondary text-sm mb-3"
      data-testid="benefitWorklist.freshness.asOf"
    >
      {asOf}
    </div>
  );
}

export default BenefitWorklistFreshness;
